// Service catalogue driving Home's service cards, /services, /services/:slug,
// the footer "Services" column, and the Contact page's service select.
//
// NOTE: challenge/capabilities/process/outcomes copy below is new draft text
// written in the site's existing voice for the redesign — please review
// before launch. Everything else (titles, summaries, the 6 automation
// deliverables, the segment cards) is carried over verbatim from the current
// Home.tsx.

import { IconId } from '../components/icons';
import { ServiceCardData } from '../components/ServiceCard';
import { ToolId } from './tools';

export type ServiceAccent = 'blue' | 'purple' | 'green' | 'orange' | 'red' | 'teal';

export type ServiceSlug = 'copilot-power-platform' | 'automation-workflows';

export interface ServiceDeliverable {
  title: string;
  description: string;
  icon: IconId;
}

export interface ServiceProcessStep {
  title: string;
  description: string;
}

export interface ServiceOutcome {
  value: string;
  label: string;
}

export interface Service {
  slug: ServiceSlug;
  title: string;
  shortTitle: string;
  icon: IconId;
  accent: ServiceAccent;
  /** Card-length copy, used on Home and the /services grid. */
  summary: string;
  /** Longer copy for the PageHeader description on the detail page. */
  description: string;
  challenge: { title: string; body: string };
  capabilities: string[];
  deliverables: ServiceDeliverable[];
  process: ServiceProcessStep[];
  outcomes?: ServiceOutcome[];
  tools: ToolId[];
  /** Ids from caseStudies.ts to show as "Selected results" on the detail page. */
  caseStudyIds?: string[];
}

export const SERVICES: Service[] = [
  {
    slug: 'copilot-power-platform',
    title: 'Copilot & Power Platform Delivery',
    shortTitle: 'Copilot & Power Platform',
    icon: 'ms-copilot',
    accent: 'green',
    summary:
      'Roadmap, governance, and production build. Custom Copilot agents, RAG pipelines, Power Apps, and Power Automate solutions, hardened and rolled out in phases.',
    description:
      'From roadmap to production: custom Copilot agents, RAG pipelines, and Power Platform apps built to run, not demo.',
    challenge: {
      title: 'Pilots that never leave the sandbox',
      body: 'Copilot Studio and Power Platform make it easy to build a demo and hard to ship something governed, secure, and maintained. We build for production from day one — security boundaries, monitoring, and a rollout plan included, not bolted on afterward.',
    },
    capabilities: [
      'Custom Copilot Studio agents and topics',
      'Retrieval-augmented generation (RAG) pipelines over your data',
      'Power Apps for line-of-business workflows',
      'Power Automate flows connecting Microsoft 365 and third-party systems',
      'Dataverse and Fabric data modelling',
      'Security, DLP, and environment governance',
    ],
    deliverables: [
      {
        title: 'Solution roadmap',
        description: 'A phased build plan with clear milestones and governance checkpoints.',
        icon: 'discovery',
      },
      {
        title: 'Production build',
        description:
          'Hardened Copilot agents, Power Apps, and automation flows, tested end to end.',
        icon: 'ms-copilot',
      },
      {
        title: 'Governance model',
        description: 'DLP policies, environment strategy, and monitoring set up before go-live.',
        icon: 'security',
      },
    ],
    process: [
      {
        title: 'Roadmap & architecture',
        description: 'Design the solution architecture and phased delivery plan.',
      },
      {
        title: 'Build',
        description: 'Develop Copilot agents, Power Apps, and automation flows in the open.',
      },
      {
        title: 'Harden & test',
        description: 'Security review, DLP policy, and end-to-end testing before rollout.',
      },
      {
        title: 'Phased rollout',
        description: 'Ship to a pilot group, then scale out with monitoring in place.',
      },
    ],
    outcomes: [
      { value: '2 – 6 Weeks', label: 'To first working automation' },
      { value: '25% – 40%', label: 'Typical efficiency gain' },
    ],
    tools: [
      'copilot-studio',
      'power-platform',
      'power-automate',
      'dataverse',
      'power-bi',
      'fabric',
      'azure-ai-services',
      'ai-builder',
    ],
    caseStudyIds: ['policy-copilot'],
  },
  {
    slug: 'automation-workflows',
    title: 'Automation Workflows',
    shortTitle: 'Automation Workflows',
    icon: 'automation',
    accent: 'orange',
    summary:
      'AI agents and workflow automation that connect the tools you already pay for — and run without anyone watching them.',
    description:
      'Automate the work nobody should be doing. AI agents and workflow automation that connect the tools you already pay for.',
    challenge: {
      title: 'The repetitive work nobody has time to fix',
      body: 'Invoice re-keying, ticket triage, lead entry, manual onboarding checklists — every team has a list of tasks everyone agrees should be automated and nobody has time to build. We connect the systems you already run, no platform migration required.',
    },
    capabilities: [
      'Document and data extraction from invoices, contracts, and forms',
      'Ticket and email classification, routing, and drafting',
      'CRM enrichment, scoring, and hygiene automation',
      'Approval routing with full context and audit trail',
      'Cross-system reporting and data synchronization',
      'Human-in-the-loop review for exceptions',
    ],
    deliverables: [
      {
        title: 'Document & Invoice Processing',
        description:
          'Extract, validate, and route data from invoices, contracts, and forms. No manual re-keying, no typos landing in your ERP.',
        icon: 'document',
      },
      {
        title: 'Customer Support Triage',
        description:
          'Classify, route, and draft replies to inbound tickets and email. Deflect the repetitive majority, escalate the rest with full context attached.',
        icon: 'support',
      },
      {
        title: 'Lead Capture & CRM Hygiene',
        description:
          'Enrich and score inbound leads, then push clean records into your CRM. Sales stops doing data entry.',
        icon: 'lead',
      },
      {
        title: 'Onboarding & Offboarding',
        description:
          'Trigger accounts, access, equipment, and paperwork from a single approval. Offboard the same way — nothing forgotten, nothing left open.',
        icon: 'onboarding',
      },
      {
        title: 'Reporting & Data Sync',
        description:
          'Keep finance, ops, and sales systems in agreement. Scheduled reports assembled and delivered without anyone opening a spreadsheet.',
        icon: 'reporting',
      },
      {
        title: 'Approvals & Exceptions',
        description:
          'Route approvals with context attached and escalate only what genuinely needs a human decision.',
        icon: 'approvals',
      },
    ],
    process: [
      {
        title: 'Map the workflow',
        description: 'Document the current manual process end to end, including exceptions.',
      },
      {
        title: 'Design the automation',
        description: 'Choose the right tool for the job — Power Automate, n8n, or a custom agent.',
      },
      {
        title: 'Build & test',
        description: 'Build against real data with human-in-the-loop checkpoints where it matters.',
      },
      {
        title: 'Deploy & monitor',
        description:
          'Ship to production with monitoring and a clear escalation path for exceptions.',
      },
    ],
    tools: [
      'power-automate',
      'n8n',
      'logic-apps',
      'make',
      'uipath',
      'copilot-studio',
      'azure-openai',
      'azure-ai-services',
    ],
    caseStudyIds: ['ocr-document-intake'],
  },
];

/** Microsoft 365 Setup has its own landing page rather than a /services/:slug
 *  detail page, so it's a card entry instead of a full Service. */
export const M365_SERVICE_CARD: ServiceCardData = {
  title: 'Microsoft 365 Setup',
  summary:
    'Professional email on your own domain, Teams, and SharePoint — set up for you, one time, starting at $500 CAD. Migrations from Google Workspace included.',
  icon: 'ms-365',
  accent: 'blue',
  to: '/microsoft-365-setup',
};

/** The three services, in display order, for card grids. */
export const SERVICE_CARDS: ServiceCardData[] = [...SERVICES, M365_SERVICE_CARD];

export function getService(slug: string | undefined): Service | undefined {
  return SERVICES.find(s => s.slug === slug);
}

export interface EngagementModel {
  title: string;
  description: string;
}

export const ENGAGEMENT_MODELS: EngagementModel[] = [
  {
    title: 'Fixed-Scope Build',
    description:
      'A defined solution, delivered on a fixed timeline and price — the right fit for a single, well-scoped use case.',
  },
  {
    title: 'Build & Run',
    description:
      'We build the solution and stay on to operate, monitor, and improve it, so nothing goes stale after go-live.',
  },
  {
    title: 'Embedded Consultant',
    description:
      'A senior consultant working directly alongside your team on an ongoing basis, for teams scaling multiple initiatives at once.',
  },
];

export interface Segment {
  label: string;
  title: string;
  points: string[];
}

export const SEGMENTS: Segment[] = [
  {
    label: 'FOR SMALL & MID-SIZED BUSINESS',
    title: 'Fast, Contained, Yours to Keep',
    points: [
      'Connects the SaaS tools you already pay for — no platform migration',
      'Live in weeks, not quarters, with fixed-scope engagements',
      'No in-house IT team required — we run it, or hand it over',
      'Self-hosted option keeps per-task costs flat as volume grows',
    ],
  },
  {
    label: 'FOR ENTERPRISE',
    title: 'Governed, Auditable, Built to Scale',
    points: [
      'Governance, audit trails, and human-in-the-loop on every workflow',
      'Canadian data residency via self-hosted deployment',
      'Integrates across your Microsoft estate and the legacy systems around it',
      'SSO and role-based access, aligned to existing security policy',
      'Predictable cost at scale versus per-seat automation licensing',
    ],
  },
];
